import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Zap, Pencil, X, Loader2, Inbox } from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = ["Body Building", "Cardio", "CrossFit", "HIIT", "Pilates", "Strength Building", "Yoga"];

export const ExerciseRequestsAdmin = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [editReq, setEditReq] = useState(null); // request being edited
  const [draft, setDraft] = useState(null);
  const [draftLoading, setDraftLoading] = useState(false);

  const load = async () => {
    try { const { data } = await api.get("/admin/exercise-requests"); setItems(data); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const pending = items.filter((r) => r.status === "pending");
  const handled = items.filter((r) => r.status !== "pending");

  const autoApprove = async (r) => {
    setBusyId(r.request_id);
    try {
      const { data } = await api.post(`/admin/exercise-requests/${r.request_id}/approve`, {});
      toast.success(data.already_existed ? `"${r.name}" was already in the library — request marked approved` : `"${r.name}" is live — AI filled the details, thumbnail generating`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Approve failed");
    } finally { setBusyId(null); }
  };

  const reject = async (r) => {
    if (!window.confirm(`Reject "${r.name}"?`)) return;
    setBusyId(r.request_id);
    try { await api.post(`/admin/exercise-requests/${r.request_id}/reject`); toast.success("Rejected"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Reject failed"); }
    finally { setBusyId(null); }
  };

  const openEdit = async (r) => {
    setEditReq(r); setDraft(null); setDraftLoading(true);
    try {
      const { data } = await api.get(`/admin/exercise-requests/${r.request_id}/draft`);
      setDraft({ ...data, muscle_groups: (data.muscle_groups || []).join(", "), instructions: (data.instructions || []).join("\n") });
    } catch (e) {
      toast.error("Couldn't build a draft — you can still fill it in manually");
      setDraft({ name: r.name, category: "Body Building", muscle_groups: "", equipment: "Bodyweight", difficulty: "Beginner", instructions: "" });
    } finally { setDraftLoading(false); }
  };

  const publishEdited = async () => {
    if (!editReq || !draft) return;
    setBusyId(editReq.request_id);
    try {
      await api.post(`/admin/exercise-requests/${editReq.request_id}/approve`, {
        name: draft.name.trim(),
        category: draft.category,
        muscle_groups: draft.muscle_groups.split(",").map((m) => m.trim()).filter(Boolean),
        equipment: draft.equipment,
        difficulty: draft.difficulty,
        instructions: draft.instructions.split("\n").map((l) => l.trim()).filter(Boolean),
      });
      toast.success(`"${draft.name}" published to the library`);
      setEditReq(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Publish failed");
    } finally { setBusyId(null); }
  };

  if (loading) return <div className="text-neutral-400 flex items-center gap-2 py-6"><Loader2 className="w-4 h-4 animate-spin" /> Loading requests…</div>;

  return (
    <div className="space-y-6">
      <div>
        <h3 className="font-display text-2xl uppercase mb-1">Exercise Requests</h3>
        <div className="text-xs text-neutral-400 mb-4">Members suggest missing exercises. Auto-approve lets AI fill details + thumbnail instantly; Edit lets you tweak first.</div>
        {pending.length === 0 ? (
          <div className="border border-dashed border-neutral-800 p-8 text-center" data-testid="exreq-empty">
            <Inbox className="w-8 h-8 text-neutral-600 mx-auto mb-3" />
            <div className="text-sm text-neutral-400">No pending requests. All caught up.</div>
          </div>
        ) : (
          <div className="divide-y divide-neutral-800 border border-neutral-800">
            {pending.map((r) => (
              <div key={r.request_id} data-testid={`exreq-row-${r.request_id}`} className="p-4 flex flex-col sm:flex-row sm:items-center gap-3">
                <div className="flex-1 min-w-0">
                  <div className="font-display text-xl uppercase truncate">{r.name}</div>
                  <div className="text-xs text-neutral-500">
                    By {r.requested_by} · {new Date(r.created_at).toLocaleDateString()}
                    {r.custom_exercise_id && <span className="ml-2 px-1.5 py-0.5 text-[9px] tracking-widest uppercase font-bold border border-blue-500/40 text-blue-500">From custom</span>}
                  </div>
                  {r.notes && <div className="text-xs text-neutral-400 mt-1 line-clamp-2">"{r.notes}"</div>}
                </div>
                <div className="flex gap-2 shrink-0">
                  <Button data-testid={`exreq-auto-${r.request_id}`} onClick={() => autoApprove(r)} disabled={busyId === r.request_id} className="bg-blue-500 hover:bg-blue-600 rounded-none uppercase tracking-wider font-display h-9 text-xs">
                    {busyId === r.request_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Zap className="w-3.5 h-3.5 mr-1" /> Auto Approve</>}
                  </Button>
                  <Button data-testid={`exreq-edit-${r.request_id}`} onClick={() => openEdit(r)} variant="outline" className="bg-transparent border-neutral-700 text-white rounded-none uppercase tracking-wider font-display h-9 text-xs">
                    <Pencil className="w-3.5 h-3.5 mr-1" /> Edit
                  </Button>
                  <Button data-testid={`exreq-reject-${r.request_id}`} onClick={() => reject(r)} disabled={busyId === r.request_id} size="icon" variant="ghost" className="text-neutral-400 hover:text-red-400 h-9 w-9">
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {handled.length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-2">Handled</div>
          <div className="divide-y divide-neutral-900 border border-neutral-800">
            {handled.slice(0, 20).map((r) => (
              <div key={r.request_id} className="flex items-center gap-2 p-2.5">
                <div className="flex-1 min-w-0">
                  <span className="text-sm font-semibold">{r.name}</span>
                  <span className="text-[10px] text-neutral-500 ml-2">by {r.requested_by}</span>
                </div>
                <span className={`px-1.5 py-0.5 text-[9px] tracking-widest uppercase font-bold border ${r.status === "approved" ? "border-emerald-500/50 text-emerald-400" : "border-neutral-600 text-neutral-500"}`}>{r.status}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Edit-before-publish dialog */}
      <Dialog open={!!editReq} onOpenChange={(o) => !o && setEditReq(null)}>
        <DialogContent className="bg-neutral-950 border-neutral-800 text-white max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl uppercase">Edit & Publish</DialogTitle>
            <DialogDescription className="text-xs text-neutral-400">AI pre-filled the details — tweak anything, then publish to the public library.</DialogDescription>
          </DialogHeader>
          {draftLoading || !draft ? (
            <div className="text-neutral-400 flex items-center gap-2 py-6"><Loader2 className="w-4 h-4 animate-spin" /> AI drafting details…</div>
          ) : (
            <div className="space-y-3">
              <div>
                <Label className="text-xs uppercase tracking-wider text-neutral-400">Name</Label>
                <Input data-testid="exreq-draft-name" value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} className="mt-1 bg-neutral-900 border-neutral-800 rounded-none h-11 text-white" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase tracking-wider text-neutral-400">Category</Label>
                  <Select value={draft.category} onValueChange={(v) => setDraft({ ...draft, category: v })}>
                    <SelectTrigger data-testid="exreq-draft-category" className="mt-1 bg-neutral-900 border-neutral-800 rounded-none h-11"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-neutral-900 border-neutral-800">
                      {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-neutral-400">Difficulty</Label>
                  <Select value={draft.difficulty} onValueChange={(v) => setDraft({ ...draft, difficulty: v })}>
                    <SelectTrigger data-testid="exreq-draft-difficulty" className="mt-1 bg-neutral-900 border-neutral-800 rounded-none h-11"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-neutral-900 border-neutral-800">
                      {["Beginner", "Intermediate", "Advanced"].map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-neutral-400">Equipment</Label>
                <Input data-testid="exreq-draft-equipment" value={draft.equipment} onChange={(e) => setDraft({ ...draft, equipment: e.target.value })} className="mt-1 bg-neutral-900 border-neutral-800 rounded-none h-11 text-white" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-neutral-400">Muscle groups (comma separated)</Label>
                <Input data-testid="exreq-draft-muscles" value={draft.muscle_groups} onChange={(e) => setDraft({ ...draft, muscle_groups: e.target.value })} className="mt-1 bg-neutral-900 border-neutral-800 rounded-none h-11 text-white" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-neutral-400">Steps (one per line)</Label>
                <Textarea data-testid="exreq-draft-instructions" value={draft.instructions} onChange={(e) => setDraft({ ...draft, instructions: e.target.value })} rows={6} className="mt-1 bg-neutral-900 border-neutral-800 rounded-none text-white text-sm" />
              </div>
              <Button data-testid="exreq-publish-btn" onClick={publishEdited} disabled={busyId === editReq?.request_id} className="w-full bg-blue-500 hover:bg-blue-600 rounded-none uppercase tracking-wider font-display h-11">
                {busyId === editReq?.request_id ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Publishing…</> : "Publish to Library"}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ExerciseRequestsAdmin;
